import { ComponentType, lazy, Suspense, useEffect } from "react";
import { THINKBOARD_ENABLED } from "../thinkboard/flags";
import { CBSE_NCERT_AI_ENABLED } from "../cbse_ncert_ai/flags";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { RequireRole, STAFF_ROLES } from "../components/RequireRole";
import { LoginPage as LoginPageComponent } from "../pages/LoginPage";
import { RegisterPage as RegisterPageComponent } from "../pages/RegisterPage";
import { useAppSelector } from "./hooks";
import { useTenantBootstrap } from "../hooks/useTenant";
import { getDefaultRouteForRole } from "../features/auth/tokenUtils";
import { withPageGuard } from "../components/withPageGuard";

const lazyPage = (importer: () => Promise<{ default: ComponentType<any> }>) => lazy(importer);
const LoginPage = withPageGuard(LoginPageComponent, "LoginPage");
const RegisterPage = withPageGuard(RegisterPageComponent, "RegisterPage");
const NotFoundPage = withPageGuard(
  lazyPage(() => import("../pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage }))),
  "NotFoundPage"
);
const DashboardPage = withPageGuard(
  lazyPage(() => import("../pages/DashboardPage").then((module) => ({ default: module.DashboardPage }))),
  "DashboardPage"
);
const QuizPlayerPage = withPageGuard(
  lazyPage(() => import("../pages/QuizPlayerPage").then((module) => ({ default: module.QuizPlayerPage }))),
  "QuizPlayerPage"
);
const StudentExamsPage = withPageGuard(
  lazyPage(() => import("../pages/StudentExamsPage").then((module) => ({ default: module.StudentExamsPage }))),
  "StudentExamsPage"
);
const MyResultsPage = withPageGuard(
  lazyPage(() => import("../pages/MyResultsPage").then((module) => ({ default: module.MyResultsPage }))),
  "MyResultsPage"
);
const QuizResultPage = withPageGuard(
  lazyPage(() => import("../pages/QuizResultPage").then((module) => ({ default: module.QuizResultPage }))),
  "QuizResultPage"
);
const GuestChallengePage = withPageGuard(
  lazyPage(() => import("../pages/GuestChallengePage").then((module) => ({ default: module.GuestChallengePage }))),
  "GuestChallengePage"
);
const ChallengePage = withPageGuard(
  lazyPage(() => import("../pages/ChallengePage").then((module) => ({ default: module.ChallengePage }))),
  "ChallengePage"
);
const InviteLandingPage = withPageGuard(
  lazyPage(() => import("../pages/InviteLandingPage").then((module) => ({ default: module.InviteLandingPage }))),
  "InviteLandingPage"
);
const AIWorkbenchPage = withPageGuard(
  lazyPage(() => import("../pages/AIWorkbenchPage").then((module) => ({ default: module.AIWorkbenchPage }))),
  "AIWorkbenchPage"
);
const ResearchStudioPage = withPageGuard(
  lazyPage(() => import("../pages/ResearchStudioPage").then((module) => ({ default: module.ResearchStudioPage }))),
  "ResearchStudioPage"
);
const ResearchStudioHelpPage = withPageGuard(
  lazyPage(() => import("../pages/ResearchStudioHelpPage").then((module) => ({ default: module.ResearchStudioHelpPage }))),
  "ResearchStudioHelpPage"
);
// Thinkboard (frontend/src/thinkboard): routed only when VITE_THINKBOARD_ENABLED is on.
const ThinkboardHomePage = withPageGuard(
  lazyPage(() => import("../thinkboard/pages/ThinkboardHomePage").then((module) => ({ default: module.ThinkboardHomePage }))),
  "ThinkboardHomePage"
);
const ThinkboardBoardPage = withPageGuard(
  lazyPage(() => import("../thinkboard/pages/ThinkboardBoardPage").then((module) => ({ default: module.ThinkboardBoardPage }))),
  "ThinkboardBoardPage"
);
const ThinkboardHelpPage = withPageGuard(
  lazyPage(() => import("../thinkboard/pages/ThinkboardHelpPage").then((module) => ({ default: module.ThinkboardHelpPage }))),
  "ThinkboardHelpPage"
);
const ThinkboardJoinPage = withPageGuard(
  lazyPage(() => import("../thinkboard/pages/ThinkboardJoinPage").then((module) => ({ default: module.ThinkboardJoinPage }))),
  "ThinkboardJoinPage"
);
const ResearchActivityPage = withPageGuard(
  lazyPage(() => import("../pages/ResearchActivityPage").then((module) => ({ default: module.ResearchActivityPage }))),
  "ResearchActivityPage"
);
const AdminAnalyticsPage = withPageGuard(
  lazyPage(() => import("../pages/AdminAnalyticsPage").then((module) => ({ default: module.AdminAnalyticsPage }))),
  "AdminAnalyticsPage"
);
const AdminUsersPage = withPageGuard(
  lazyPage(() => import("../pages/AdminUsersPage").then((module) => ({ default: module.AdminUsersPage }))),
  "AdminUsersPage"
);
const SchoolClassesPage = withPageGuard(
  lazyPage(() => import("../pages/SchoolPages").then((module) => ({ default: module.SchoolClassesPage }))),
  "SchoolClassesPage"
);
const SchoolTeachersPage = withPageGuard(
  lazyPage(() => import("../pages/SchoolPages").then((module) => ({ default: module.SchoolTeachersPage }))),
  "SchoolTeachersPage"
);
// The CBSE / NCERT course (frontend/src/cbse_ncert_ai): routed only when VITE_CBSE_NCERT_AI_ENABLED is on.
const CourseSyllabusPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/SyllabusPage").then((module) => ({ default: module.SyllabusPage }))),
  "CourseSyllabusPage"
);
const CourseListPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/CoursesPage").then((module) => ({ default: module.CoursesPage }))),
  "CourseListPage"
);
const CourseDetailPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/CoursesPage").then((module) => ({ default: module.CoursePage }))),
  "CourseDetailPage"
);
const CourseTeachPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/TeachPage").then((module) => ({ default: module.TeachPage }))),
  "CourseTeachPage"
);
const CourseProgressPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/TeachPage").then((module) => ({ default: module.ProgressPage }))),
  "CourseProgressPage"
);
const CourseLivePage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/LiveLessonPage").then((module) => ({ default: module.LiveLessonPage }))),
  "CourseLivePage"
);
const CoursePracticePage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/PracticePage").then((module) => ({ default: module.PracticePage }))),
  "CoursePracticePage"
);
const CourseReadinessPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/ReadinessPage").then((module) => ({ default: module.TeacherReadinessPage }))),
  "CourseReadinessPage"
);
const CourseSchoolsReadinessPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/ReadinessPage").then((module) => ({ default: module.SchoolsReadinessPage }))),
  "CourseSchoolsReadinessPage"
);
const CourseClassPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/ClassLessonPage").then((module) => ({ default: module.ClassLessonPage }))),
  "CourseClassPage"
);
const CourseMinePage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/MyCoursePage").then((module) => ({ default: module.MyCoursePage }))),
  "CourseMinePage"
);
const CourseNotebookPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/NotebookPage").then((module) => ({ default: module.NotebookPage }))),
  "CourseNotebookPage"
);
const CoursePromptLabPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/PromptLabPage").then((module) => ({ default: module.PromptLabPage }))),
  "CoursePromptLabPage"
);
const CoursePromptLabClassPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/PromptLabClassPage").then((module) => ({ default: module.PromptLabClassPage }))),
  "CoursePromptLabClassPage"
);
const TrainingPage = withPageGuard(
  lazyPage(() => import("../features/evidence/EvidencePages").then((module) => ({ default: module.TrainingPage }))),
  "TrainingPage"
);
const PortfolioPage = withPageGuard(
  lazyPage(() => import("../features/evidence/EvidencePages").then((module) => ({ default: module.PortfolioPage }))),
  "PortfolioPage"
);
const CertificatesPage = withPageGuard(
  lazyPage(() => import("../features/evidence/EvidencePages").then((module) => ({ default: module.CertificatesPage }))),
  "CertificatesPage"
);
const VerifyCertificatePage = lazyPage(() => import("../features/evidence/EvidencePages").then((module) => ({ default: module.VerifyCertificatePage })));
const CompliancePage = withPageGuard(
  lazyPage(() => import("../features/compliance/CompliancePage").then((module) => ({ default: module.CompliancePage }))),
  "CompliancePage"
);
const WellbeingPage = withPageGuard(
  lazyPage(() => import("../features/wellbeing/WellbeingPage").then((module) => ({ default: module.WellbeingPage }))),
  "WellbeingPage"
);
const CourseNotebooksPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/NotebooksPage").then((module) => ({ default: module.NotebooksPage }))),
  "CourseNotebooksPage"
);
const CourseNotebookPrintPage = withPageGuard(
  lazyPage(() => import("../cbse_ncert_ai/pages/NotebookPrintPage").then((module) => ({ default: module.NotebookPrintPage }))),
  "CourseNotebookPrintPage"
);
const ApprovalsPage = withPageGuard(
  lazyPage(() => import("../pages/ApprovalsPage").then((module) => ({ default: module.ApprovalsPage }))),
  "ApprovalsPage"
);
const ParentalConsentPage = withPageGuard(
  lazyPage(() => import("../pages/ParentalConsentPage").then((module) => ({ default: module.ParentalConsentPage }))),
  "ParentalConsentPage"
);
const SchoolAssignmentsPage = withPageGuard(
  lazyPage(() => import("../pages/SchoolWorkPages").then((module) => ({ default: module.SchoolAssignmentsPage }))),
  "SchoolAssignmentsPage"
);
const SchoolReportsPage = withPageGuard(
  lazyPage(() => import("../pages/SchoolWorkPages").then((module) => ({ default: module.SchoolReportsPage }))),
  "SchoolReportsPage"
);
const MyWorkPage = withPageGuard(
  lazyPage(() => import("../pages/SchoolWorkPages").then((module) => ({ default: module.MyWorkPage }))),
  "MyWorkPage"
);
const OrganisationsReportPage = withPageGuard(
  lazyPage(() => import("../pages/SchoolWorkPages").then((module) => ({ default: module.OrganisationsReportPage }))),
  "OrganisationsReportPage"
);
const ManageStudentsPage = withPageGuard(
  lazyPage(() => import("../pages/ManageStudentsPage").then((module) => ({ default: module.ManageStudentsPage }))),
  "ManageStudentsPage"
);
const ManageCoachingsPage = withPageGuard(
  lazyPage(() => import("../pages/ManagementPlaceholders").then((module) => ({ default: module.ManageCoachingsPage }))),
  "ManageCoachingsPage"
);
const ManageExamsPage = withPageGuard(
  lazyPage(() => import("../pages/ManagementPlaceholders").then((module) => ({ default: module.ManageExamsPage }))),
  "ManageExamsPage"
);
const ManageSubjectsPage = withPageGuard(
  lazyPage(() => import("../pages/ManagementPlaceholders").then((module) => ({ default: module.ManageSubjectsPage }))),
  "ManageSubjectsPage"
);
const ManageQuizzesPage = withPageGuard(
  lazyPage(() => import("../pages/ManagementPlaceholders").then((module) => ({ default: module.ManageQuizzesPage }))),
  "ManageQuizzesPage"
);
const CreateQuizPage = withPageGuard(
  lazyPage(() => import("../pages/ManagementPlaceholders").then((module) => ({ default: module.CreateQuizPage }))),
  "CreateQuizPage"
);
const TranslationReviewPage = withPageGuard(
  lazyPage(() => import("../pages/TranslationReviewPage").then((module) => ({ default: module.TranslationReviewPage }))),
  "TranslationReviewPage"
);
/** What fills the window while a lazily-loaded route chunk arrives.
 *
 *  Mirrors the boot skeleton in index.html on purpose: the two are the same
 *  moment from the user's point of view — "the app is coming" — and showing two
 *  different placeholders for it makes a single navigation look like two. */
const RouteFallback = () => (
  <div className="route-fallback" role="status" aria-live="polite">
    <span className="visually-hidden">Loading…</span>
    <div className="route-fallback__bar" aria-hidden="true" />
    <div className="route-fallback__body" aria-hidden="true" />
  </div>
);

const StudentPapersPage = withPageGuard(
  lazyPage(() => import("../pages/ManagementPlaceholders").then((module) => ({ default: module.StudentPapersPage }))),
  "StudentPapersPage"
);
const ManageReviewsPage = withPageGuard(
  lazyPage(() => import("../pages/ManagementPlaceholders").then((module) => ({ default: module.ManageReviewsPage }))),
  "ManageReviewsPage"
);
const QuizResultsAdminPage = withPageGuard(
  lazyPage(() =>
    import("../pages/QuizResultsAdminPage").then((module) => ({ default: module.QuizResultsAdminPage }))
  ),
  "QuizResultsAdminPage"
);
const KeyVerificationPage = withPageGuard(
  lazyPage(() =>
    import("../pages/KeyVerificationPage").then((module) => ({
      default: module.KeyVerificationPage
    }))
  ),
  "KeyVerificationPage"
);
const SolutionMethodReviewPage = withPageGuard(
  lazyPage(() =>
    import("../pages/SolutionMethodReviewPage").then((module) => ({
      default: module.SolutionMethodReviewPage
    }))
  ),
  "SolutionMethodReviewPage"
);
const DoubtSolverPage = withPageGuard(
  lazyPage(() => import("../pages/DoubtSolverPage").then((module) => ({ default: module.DoubtSolverPage }))),
  "DoubtSolverPage"
);
const ProfileSettingsPage = withPageGuard(
  lazyPage(() => import("../pages/ProfileSettingsPage").then((module) => ({ default: module.ProfileSettingsPage }))),
  "ProfileSettingsPage"
);

const ArtefactPage = withPageGuard(
  lazyPage(() => import("../pages/ArtefactPage").then((module) => ({ default: module.ArtefactPage }))),
  "ArtefactPage"
);

const StudyMaterialPage = withPageGuard(
  lazyPage(() => import("../pages/StudyMaterialPage").then((module) => ({ default: module.StudyMaterialPage }))),
  "StudyMaterialPage"
);
const MyNotesPage = withPageGuard(
  lazyPage(() => import("../pages/MyNotesPage").then((module) => ({ default: module.MyNotesPage }))),
  "MyNotesPage"
);
const NotebookPage = withPageGuard(
  lazyPage(() => import("../pages/NotebookPage").then((module) => ({ default: module.NotebookPage }))),
  "NotebookPage"
);
const LearningLibraryPage = withPageGuard(
  lazyPage(() => import("../pages/LearningLibraryPage").then((module) => ({ default: module.LearningLibraryPage }))),
  "LearningLibraryPage"
);
const ApiTimingsPage = withPageGuard(
  lazyPage(() => import("../pages/ApiTimingsPage").then((module) => ({ default: module.ApiTimingsPage }))),
  "ApiTimingsPage"
);

const AIModelSettingsPage = withPageGuard(
  lazyPage(() => import("../pages/AIModelSettingsPage").then((module) => ({ default: module.AIModelSettingsPage }))),
  "AIModelSettingsPage"
);
const HomePage = withPageGuard(
  lazyPage(() => import("../pages/HomePage").then((module) => ({ default: module.HomePage }))),
  "HomePage"
);
type AuthSliceShape = {
  auth?: {
    accessToken: string | null;
    profile?: {
      role: string | null;
    } | null;
  };
};

const RouterShell = () => {
  const token = useAppSelector<string | null>((state) => (state as AuthSliceShape).auth?.accessToken ?? null);
  const role = useAppSelector<string | null>((state) => (state as AuthSliceShape).auth?.profile?.role ?? null);
  useTenantBootstrap();

  useEffect(() => {
    const warmup = () => {
      void Promise.all([
        import("../pages/DashboardPage"),
        import("../pages/QuizPlayerPage"),
        import("../pages/InviteLandingPage"),
        import("../pages/AIWorkbenchPage"),
        import("../pages/AdminUsersPage"),
        import("../pages/NotFoundPage")
      ]);
    };
    const win = window as typeof window & {
      requestIdleCallback?: (callback: IdleRequestCallback) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    if (typeof win.requestIdleCallback === "function") {
      const handle = win.requestIdleCallback(warmup);
      return () => win.cancelIdleCallback?.(handle);
    }
    const timeout = window.setTimeout(warmup, 250);
    return () => window.clearTimeout(timeout);
  }, []);

  // A VISIBLE fallback, not a screen-reader-only one.
  //
  // Every page in this router is lazy, so this fallback is what fills the
  // window while a route chunk downloads. As `sr-only` it rendered nothing at
  // all, which meant a route change showed an empty page — the same blank the
  // boot skeleton in index.html exists to prevent, reappearing on every
  // navigation. `RouteFallback` paints the same furniture, so the sequence is
  // shell -> shell -> content instead of shell -> blank -> content.
  //
  // `role="status"` and the visually-hidden label are kept so assistive tech
  // still hears that something is loading.
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={token ? <Navigate to={getDefaultRouteForRole(role)} replace /> : <HomePage />} />
  <Route path="/auth" element={<LoginPage />} />
  {/* Public on purpose: a parent or another school checks a course certificate's code. */}
  <Route path="/verify/:code" element={<VerifyCertificatePage />} />
  <Route path="/register" element={<RegisterPage />} />
  <Route path="/quizzes/invites/:code" element={<InviteLandingPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/exams" element={<StudentExamsPage />} />
          <Route path="/quiz" element={<QuizPlayerPage />} />
          <Route path="/quiz/:quizId" element={<QuizPlayerPage />} />
          <Route path="/results" element={<MyResultsPage />} />
          <Route path="/results/:quizId" element={<QuizResultPage />} />
          <Route path="/challenge/:quizId" element={<ChallengePage />} />
          <Route path="/challenge-play/:code" element={<GuestChallengePage />} />
          <Route path="/doubts" element={<DoubtSolverPage />} />
          {/* The conversation id lives IN THE URL, which is how ChatGPT does it
              (`/c/<id>`) and why a refresh there keeps you where you were. It
              also makes back and forward move between conversations, and a
              link to one shareable with a colleague — none of which component
              state can provide. */}
          <Route path="/doubts/:conversationId" element={<DoubtSolverPage />} />
          {/* Every authenticated role can manage their own AI connections. */}
          <Route path="/settings/profile" element={<ProfileSettingsPage />} />
          <Route path="/study-material" element={<StudyMaterialPage />} />
          <Route path="/learning-library" element={<LearningLibraryPage />} />
          <Route path="/notes" element={<MyNotesPage />} />
          <Route path="/notes/:kind/:id" element={<NotebookPage />} />
          {/* Not for coaching admins for now (owner's decision, 14-Sep-2026). */}
          <Route element={<RequireRole allow={["super_admin", "student", "user"]} />}>
            <Route path="/research-studio" element={<ResearchStudioPage />} />
            <Route path="/research-studio/help" element={<ResearchStudioHelpPage />} />
          </Route>
          {/* Every role: staff share boards with all their students, students
              share with classmates (spec §13). The API enforces who sees what. */}
          {THINKBOARD_ENABLED && (
            <>
              <Route path="/thinkboard" element={<ThinkboardHomePage />} />
              <Route path="/thinkboard/help" element={<ThinkboardHelpPage />} />
              <Route path="/thinkboard/join/:code" element={<ThinkboardJoinPage />} />
              <Route path="/thinkboard/:boardId" element={<ThinkboardBoardPage />} />
            </>
          )}
          <Route path="/artefacts/:artefactId" element={<ArtefactPage />} />
          <Route path="/settings/ai-models" element={<AIModelSettingsPage />} />
          {/* Admin-only in the API as well as here: the rows carry actor ids
              and every path a user touched. */}
          {/* STAFF ONLY, AND ONLY THESE.
              Until now nothing gated a route by role: `ProtectedRoute` checks a
              token and `withPageGuard` is an error boundary. A student who
              typed `/manage/users` stayed there and was shown the admin page's
              heading and an empty table with no explanation. The data was never
              exposed — every endpoint behind these screens answers a student
              with 403, verified against the running server — this is the half
              that tells them.

              WHAT IS DELIBERATELY OUTSIDE THIS GUARD
              `/manage/quizzes`, `/manage/quizzes/new` and the `:quizId/edit`
              route are in the STUDENT sidebar as "My papers" and "Build a
              paper": the self-practice builder shares the admin's screen. They
              are left open on purpose, and gating them would delete the
              feature. */}
          <Route element={<RequireRole allow={STAFF_ROLES} />}>
            <Route path="/settings/api-timings" element={<ApiTimingsPage />} />
            <Route path="/ai-workbench" element={<AIWorkbenchPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            {/* Platform owner only: coaching admins no longer get Analytics or
                Research activity (owner's decision, 14-Sep-2026). */}
            <Route element={<RequireRole allow={["super_admin"]} />}>
              <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
              <Route path="/admin/research-activity" element={<ResearchActivityPage />} />
            </Route>
            <Route path="/manage/users" element={<ManageStudentsPage />} />
            <Route path="/manage/coachings" element={<ManageCoachingsPage />} />
            <Route path="/manage/exams" element={<ManageExamsPage />} />
            <Route path="/manage/subjects" element={<ManageSubjectsPage />} />
            <Route path="/manage/translations" element={<TranslationReviewPage />} />
            <Route path="/manage/student-papers" element={<StudentPapersPage />} />
            <Route path="/manage/reviews" element={<ManageReviewsPage />} />
            <Route
              path="/manage/quizzes/:quizId/results"
              element={<QuizResultsAdminPage />}
            />
            <Route
              path="/manage/quizzes/:quizId/key-check"
              element={<KeyVerificationPage />}
            />
            <Route
              path="/manage/quizzes/:quizId/solutions"
              element={<SolutionMethodReviewPage />}
            />
            <Route
              path="/manage/quizzes/:quizId/results/:studentId"
              element={<QuizResultPage />}
            />
          </Route>

          {/* The student's own paper builder, shared with the admin's. */}
          <Route path="/manage/quizzes" element={<ManageQuizzesPage />} />
          <Route path="/manage/quizzes/new" element={<CreateQuizPage />} />
          {/* The SAME builder, reopened on an existing paper. Not a second
              screen: two components writing one document is how an edit form
              ends up accepting a shape the create form cannot produce. The
              server allows the write only while the paper is a draft. */}
          <Route path="/manage/quizzes/:quizId/edit" element={<CreateQuizPage />} />
          {/* A school's own screens (plan step 3). A teacher sees only their
              sections there; the server scopes it either way. */}
          <Route element={<RequireRole allow={["school_admin", "teacher"]} />}>
            <Route path="/school/classes" element={<SchoolClassesPage />} />
            <Route path="/school/assignments" element={<SchoolAssignmentsPage />} />
            <Route path="/school/reports" element={<SchoolReportsPage />} />
            {CBSE_NCERT_AI_ENABLED && (
              <>
                <Route path="/course" element={<CourseListPage />} />
                <Route path="/course/syllabus" element={<CourseSyllabusPage />} />
                <Route path="/course/teach" element={<CourseTeachPage />} />
                <Route path="/course/progress" element={<CourseProgressPage />} />
                <Route path="/course/live/:deliveryId" element={<CourseLivePage />} />
                <Route path="/course/practice/:practiceId" element={<CoursePracticePage />} />
                <Route path="/course/readiness" element={<CourseReadinessPage />} />
                <Route path="/course/notebooks" element={<CourseNotebooksPage />} />
                <Route path="/course/prompt-lab/class" element={<CoursePromptLabClassPage />} />
                <Route path="/course/:courseId" element={<CourseDetailPage />} />
              </>
            )}
          </Route>
          {/* Registrations waiting for approval (plan 5e); each approver sees
              only their own organisation's (a teacher, their sections'). */}
          <Route element={<RequireRole allow={["super_admin", "coaching_admin", "school_admin", "teacher"]} />}>
            <Route path="/approvals" element={<ApprovalsPage />} />
          </Route>
          {/* Wellbeing hub: settings (org admin), counsellor desk (named counsellors), report. Server-checked. */}
          <Route element={<RequireRole allow={["school_admin", "coaching_admin", "teacher", "user"]} />}>
            <Route path="/wellbeing" element={<WellbeingPage />} />
            <Route path="/training" element={<TrainingPage />} />
          </Route>
          <Route element={<RequireRole allow={["school_admin", "coaching_admin", "teacher", "user", "student"]} />}>
            <Route path="/portfolio" element={<PortfolioPage />} />
          </Route>
          <Route element={<RequireRole allow={["school_admin", "coaching_admin"]} />}>
            <Route path="/compliance" element={<CompliancePage />} />
          </Route>
          {/* Parental consent (DPDP Act 2023): the enrolling organisation's admin records it. */}
          <Route element={<RequireRole allow={["coaching_admin", "school_admin"]} />}>
            <Route path="/consents" element={<ParentalConsentPage />} />
          </Route>
          <Route element={<RequireRole allow={["student"]} />}>
            <Route path="/my-work" element={<MyWorkPage />} />
            {CBSE_NCERT_AI_ENABLED && <Route path="/course/class" element={<CourseClassPage />} />}
            {CBSE_NCERT_AI_ENABLED && <Route path="/course/mine" element={<CourseMinePage />} />}
            {CBSE_NCERT_AI_ENABLED && <Route path="/course/notebook" element={<CourseNotebookPage />} />}
            {CBSE_NCERT_AI_ENABLED && <Route path="/course/prompt-lab" element={<CoursePromptLabPage />} />}
            {CBSE_NCERT_AI_ENABLED && <Route path="/course/certificates" element={<CertificatesPage />} />}
          </Route>
          <Route element={<RequireRole allow={["super_admin"]} />}>
            <Route path="/admin/organisations" element={<OrganisationsReportPage />} />
            {CBSE_NCERT_AI_ENABLED && <Route path="/course/schools-readiness" element={<CourseSchoolsReadinessPage />} />}
          </Route>
          {/* One student's notebook, printable: the student, their teachers or the school admin (server-checked). */}
          <Route element={<RequireRole allow={["school_admin", "teacher", "student"]} />}>
            {CBSE_NCERT_AI_ENABLED && <Route path="/course/notebook/print/:studentId" element={<CourseNotebookPrintPage />} />}
          </Route>
          <Route element={<RequireRole allow={["school_admin"]} />}>
            <Route path="/school/teachers" element={<SchoolTeachersPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export const AppRouter = () => (
  <BrowserRouter>
    <RouterShell />
  </BrowserRouter>
);
